import {
  AttachmentFileBase,
  MessageContent,
  MessagesResponse,
  Uri,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useChatConfig } from 'chat/config/queries/get'
import { FileUploadStatus } from 'chat/send_message_context'
import keys from 'chat/shared/queries/keys'
import {
  appendFileToFormData,
  uploadFileToPresignedUrl,
} from 'chat/shared/util'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { isImageType } from 'file_folder_repository/util'
import { isNil } from 'lodash'
import Path from 'path'
import { Platform } from 'react-native'
import { generateImageThumbnail } from 'shared/image_thumbnail'
import { useGetPresignedUrls } from 'shared/queries/get_presigned_url'
import Url from 'url'
import { addMessagesDataToCache } from './shared/utils'

type Variables = {
  uri?: Uri
  content: MessageContent
  conversationId?: string
  setFileUploadStatus?: (status: FileUploadStatus) => void
  isFileSharing?: boolean
}

// Define the MessageAttachment type
interface MessageAttachment {
  file_name: string | undefined
  mime_type: string
  url_uuid: string
  thumbnail_width?: number
  thumbnail_height?: number
}

export function useCreateFastUploadMessage() {
  const { server } = useServer()
  const { data: chatConfig } = useChatConfig()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()
  const getPresignedUrlsMutation = useGetPresignedUrls()

  return useMutation({
    mutationFn: createMessage,
    onSuccess: async (event, variables) => {
      if (!event) {
        return
      }
      await addMessagesDataToCache(client, [event], chatConfig)
      sendWebsocketMessage({
        action: 'sendMessageEvent',
        messageEvent: event,
      })
      await client.invalidateQueries({
        queryKey: keys.readByCounts(String(event.conversation_id)),
      })
    },
  })

  async function createMessage({
    conversationId,
    content,
    setFileUploadStatus,
  }: Variables) {
    if (!content.attachment || !conversationId) {
      return
    }

    try {
      // Set initial upload status
      if (setFileUploadStatus) {
        setFileUploadStatus({ progress: 0 })
      }

      // Get presigned URL
      const presignedUrls = await getPresignedUrlsMutation.mutateAsync({
        conversationId,
        count: 1,
        isFileSharing: false,
      })

      if (presignedUrls && presignedUrls.length > 0) {
        const presignedUrl = presignedUrls[0]
        const { name, uri: fileUri, mimeType } = content.attachment

        // Upload the file to the presigned URL
        if (setFileUploadStatus) {
          setFileUploadStatus({
            progress: 0.1,
          })
        }

        // Upload the main file
        await uploadFileToPresignedUrl(
          presignedUrl.url,
          fileUri,
          mimeType,
          progress => {
            if (setFileUploadStatus) {
              // Scale progress from 10% to 60% for main file upload
              const scaledProgress = 0.1 + 0.5 * progress
              setFileUploadStatus({
                progress: scaledProgress,
              })
            }
          }
        )

        // Upload thumbnail if a thumbnail URL is provided
        let thumbnailUri: string | undefined | null = null
        let thumbnailWidth: number | undefined
        let thumbnailHeight: number | undefined
        if (presignedUrl.thumbnail_url) {
          if (setFileUploadStatus) {
            setFileUploadStatus({
              progress: 0.6,
            })
          }

          try {
            // Generate thumbnail based on file type
            if (isImageType(mimeType)) {
              // For image files, use our new image thumbnail generator
              const thumbnailResult = await generateImageThumbnail({
                imageUri: fileUri,
              })
              thumbnailUri = thumbnailResult.uri
              thumbnailWidth = thumbnailResult.width
              thumbnailHeight = thumbnailResult.height
            }

            // If we have a thumbnail, upload it
            if (thumbnailUri) {
              if (setFileUploadStatus) {
                setFileUploadStatus({
                  progress: 0.7,
                })
              }

              // Upload the thumbnail (non-fatal if it fails)
              await uploadFileToPresignedUrl(
                presignedUrl.thumbnail_url,
                thumbnailUri,
                'image/jpeg',
                progress => {
                  if (setFileUploadStatus) {
                    // Scale progress from 70% to 80% for thumbnail upload
                    const scaledProgress = 0.7 + 0.1 * progress
                    setFileUploadStatus({
                      progress: scaledProgress,
                    })
                  }
                }
              )
            }
          } catch (error) {
            console.warn('Error handling thumbnail:', error)
            // Continue with the message even if thumbnail fails
          }
        }

        // Update status to preparing message
        if (setFileUploadStatus) {
          setFileUploadStatus({
            progress: 0.8,
          })
        }

        // Create the message attachment data
        const messageAttachment: MessageAttachment = {
          file_name:
            name ??
            (Platform.OS !== 'web'
              ? Path.basename(Url.parse(fileUri).pathname ?? '')
              : undefined),
          mime_type: mimeType,
          url_uuid: presignedUrl.uuid,
          thumbnail_width: thumbnailWidth,
          thumbnail_height: thumbnailHeight,
        }

        // Update upload status
        if (setFileUploadStatus) {
          setFileUploadStatus({ progress: 0.8 })
        }

        // Create a FormData object for the message
        const messageForm = new FormData()
        messageForm.append('body', content.body ?? '')
        if (content.mentionedUserIds) {
          messageForm.append(
            'mentioned_user_ids',
            JSON.stringify(content.mentionedUserIds)
          )
        }

        // Convert JSON to string and add as attachment_data_json parameter
        const jsonString = JSON.stringify(messageAttachment)
        messageForm.append('attachment_data_json', jsonString)

        if (!isNil(content.replyToMessageContent)) {
          messageForm.append(
            'reply_to_message_json',
            JSON.stringify(content.replyToMessageContent)
          )
        }

        // Send the message with attachment reference
        const result = await server.post<MessagesResponse.AddMessageEvent>(
          `/v2/chat/conversations/${conversationId}/messages`,
          messageForm,
          'user',
          'multipart/form-data',
          progressEvent => {
            if (setFileUploadStatus) {
              // Scale progress from 80% to 100%
              const scaledProgress =
                0.8 +
                0.2 *
                  Math.min(1, progressEvent.loaded / (progressEvent.total ?? 1))
              setFileUploadStatus({ progress: scaledProgress })
            }
          }
        )

        // Complete
        if (setFileUploadStatus) {
          setFileUploadStatus({ progress: 1 })
        }

        return result
      }
    } catch (error) {
      console.error('Error uploading attachment:', error)
      if (setFileUploadStatus) {
        setFileUploadStatus({ error: error as Error })
      }
      throw error
    }
  }
}

export async function getMessageForm(content: MessageContent) {
  const form = new FormData()

  if (content.attachment) {
    const attachment = content.attachment
    const formFieldName = 'attachment'
    const file: AttachmentFileBase = {
      name:
        attachment.name ??
        (Platform.OS !== 'web'
          ? Path.basename(Url.parse(attachment.uri).pathname ?? '')
          : undefined),
      uri: attachment.uri,
      mimeType: attachment.mimeType,
    }
    await appendFileToFormData(form, formFieldName, file)
  }
  form.append('body', content.body ?? '')

  return form
}
